/**
 * The deals and files the Files screens show, from one folder-tree read.
 *
 * A deal is a root folder that belongs to a project — the same rule the
 * deal-room sidebar and the add-files flow use. Its files are flattened from
 * every sub-folder, each carrying the folder path it sits in. The tree comes
 * from the shared folder-tree store: screens mounted one after another reuse
 * one answer, and `USER_FILES_UPDATED_EVENT` (an upload finishing, a delete)
 * refetches it once for all of them, so the screens never show a stale list.
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { UNTYPED_DOCUMENT_KEY } from "../config/documentTypes";
import type { FileInfo, FolderTreeNode, UserRef } from "../services/fileApi";
import {
	getFolderTreeSnapshot,
	loadFolderTree,
	subscribeFolderTree,
} from "../services/folderTreeStore";

export interface LibraryFile extends FileInfo {
	/** The deal's root folder id. */
	dealId: number;
	dealName: string;
	/** Sub-folder names below the deal root, outermost first. */
	folderPath: string[];
	/** `document.document_type`, or `UNTYPED_DOCUMENT_KEY`. */
	documentType: string;
	/**
	 * Whose deal (and so whose file) this is: the root folder's owner. Absent on
	 * a backend that predates Deal Room sharing.
	 */
	dealOwner?: UserRef | null;
}

export interface LibraryDeal {
	id: number;
	name: string;
	node: FolderTreeNode;
	files: LibraryFile[];
	/** The most recently added file, if any. */
	latest: LibraryFile | null;
	/** Newest file's `created_at`, else the folder's `updated_at`. */
	lastChangeAt: string;
}

/** A deal row on the Files home, with its search hits when a search is on. */
export interface DealRow {
	deal: LibraryDeal;
	matchCount?: number;
}

/** Deals whose name, or any file's name, contains the query. */
export function filterDeals(deals: LibraryDeal[], query: string): DealRow[] {
	const q = query.trim().toLowerCase();
	if (!q) return deals.map((deal) => ({ deal }));
	return deals.flatMap((deal) => {
		const matchCount = deal.files.filter((f) =>
			f.filename.toLowerCase().includes(q),
		).length;
		if (!deal.name.toLowerCase().includes(q) && matchCount === 0) return [];
		return [{ deal, matchCount }];
	});
}

export interface FileLibrary {
	deals: LibraryDeal[];
	loading: boolean;
	error: string | null;
	refresh: () => Promise<void>;
	findDeal: (dealId: number) => LibraryDeal | undefined;
	findFile: (fileId: number) => LibraryFile | undefined;
}

const isDeal = (node: FolderTreeNode): boolean =>
	Boolean(node.folder.is_root && node.folder.project_id);

const byNewest = (a: { created_at: string }, b: { created_at: string }) =>
	Date.parse(b.created_at) - Date.parse(a.created_at);

function collectFiles(
	node: FolderTreeNode,
	deal: { id: number; name: string; owner?: UserRef | null },
	path: string[],
	out: LibraryFile[],
): void {
	for (const file of node.files ?? []) {
		out.push({
			...file,
			dealId: deal.id,
			dealName: deal.name,
			dealOwner: deal.owner ?? null,
			folderPath: path,
			documentType: file.document?.document_type || UNTYPED_DOCUMENT_KEY,
		});
	}
	for (const sub of node.sub_folders ?? []) {
		collectFiles(sub, deal, [...path, sub.folder.name], out);
	}
}

export function buildLibrary(tree: FolderTreeNode[]): LibraryDeal[] {
	return tree.filter(isDeal).map((node) => {
		const deal = { id: node.folder.id, name: node.folder.name };
		const files: LibraryFile[] = [];
		collectFiles(node, { ...deal, owner: node.folder.owner }, [], files);
		files.sort(byNewest);
		const latest = files[0] ?? null;
		return {
			...deal,
			node,
			files,
			latest,
			lastChangeAt: latest?.created_at ?? node.folder.updated_at,
		};
	});
}

export function useFileLibrary(): FileLibrary {
	const snapshot = useSyncExternalStore(
		subscribeFolderTree,
		getFolderTreeSnapshot,
	);
	const tree = useMemo(() => snapshot.tree ?? [], [snapshot.tree]);
	const loading = snapshot.tree === null && !snapshot.error;
	const error = snapshot.error
		? "Couldn't load your files. Try again in a moment."
		: null;

	const refresh = useCallback(async () => {
		try {
			await loadFolderTree({ fresh: true });
		} catch (err) {
			// Recorded on the snapshot, which `error` reads.
			console.error("Failed to load files:", err);
		}
	}, []);

	// A recent tree (another Deal Room page just fetched it) renders at once;
	// otherwise this joins or starts the request. Updates arrive through the
	// store, which refetches on `USER_FILES_UPDATED_EVENT`.
	useEffect(() => {
		loadFolderTree().catch((err) => {
			console.error("Failed to load files:", err);
		});
	}, []);

	const deals = useMemo(
		() =>
			buildLibrary(tree).sort(
				(a, b) => Date.parse(b.lastChangeAt) - Date.parse(a.lastChangeAt),
			),
		[tree],
	);

	const findDeal = useCallback(
		(dealId: number) => deals.find((d) => d.id === dealId),
		[deals],
	);
	const findFile = useCallback(
		(fileId: number) => {
			for (const deal of deals) {
				const hit = deal.files.find((f) => f.id === fileId);
				if (hit) return hit;
			}
			return undefined;
		},
		[deals],
	);

	return { deals, loading, error, refresh, findDeal, findFile };
}
